import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useId,
    useState,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import Button from "../../atoms/Button";
import IconButton from "../../atoms/IconButton";
import ChevronIcon from "../../atoms/icons/ChevronIcon";
import GlassSelect from "../../atoms/GlassSelect";
import GlassDatePicker from "../../molecules/GlassDatePicker";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import ColorTokenPicker from "../../molecules/ColorTokenPicker";
import EventParticipantsPicker from "../../molecules/EventParticipantsPicker";
import SegmentedTabs from "../../molecules/SegmentedTabs";
import CalendarMonthGrid from "../../molecules/CalendarMonthGrid";
import CalendarNextEventCard from "../../molecules/CalendarNextEventCard";
import CalendarThisWeekList from "../../molecules/CalendarThisWeekList";
import useEventsCalendarMonth from "../../../hooks/useEventsCalendarMonth";
import { getEventsInNextDays, getUpcomingEvents } from "../../../utils/eventsCalendar";
import { resolveCalendarColor } from "../../../constants/calendarColors";
import {
    ensureDate,
    defaultTimedEventWindow,
    toDateInputValue,
    toDateTimeInputValue,
} from "../../pages/BandsPage/utils/orbitPlannerUtils";

import "../../../styles/vendor/fullcalendar.css";
import "./BandCalendarPanel.scss";

const MONTH_VIEW_ID = "dayGridMonth";

const RESPONSE_TO_VIEWER_STATUS = {
    ACCEPTED: "GOING",
    TENTATIVE: "INTERESTED",
    DECLINED: "DECLINED",
    PENDING: "PENDING",
};

function mapBandEventToCalendarEvent(event, normalizedCurrentUserId) {
    if (!event || !event.id) return null;
    const attendees = Array.isArray(event.attendees) ? event.attendees : [];
    const selfAttendee = normalizedCurrentUserId
        ? attendees.find((attendee) => String(attendee?.userId) === normalizedCurrentUserId)
        : null;
    const responseStatus = selfAttendee?.responseStatus || null;
    const viewerAttendanceStatus = responseStatus
        ? RESPONSE_TO_VIEWER_STATUS[responseStatus] || null
        : null;
    return {
        id: String(event.id),
        title: event.title || "",
        startAt: event.startAt,
        endAt: event.endAt,
        allDay: Boolean(event.allDay),
        timezone: event.timezone,
        status: event.status || "PUBLISHED",
        viewerAttendanceStatus,
    };
}

function resolveRangeAnchorDate(range) {
    const start = range?.start ? ensureDate(range.start) : null;
    if (!start) {
        return null;
    }
    const end = range?.end ? ensureDate(range.end) : null;
    if (end && end.getTime() > start.getTime()) {
        return new Date(start.getTime() + ((end.getTime() - start.getTime()) / 2));
    }
    return start;
}

function normalizeCalendarRange(start, end) {
    const normalizedStart = ensureDate(start);
    if (!normalizedStart) {
        return null;
    }
    const normalizedEnd = ensureDate(end);
    return {
        start: normalizedStart,
        end: normalizedEnd || normalizedStart,
    };
}

function startOfLocalDay(date) {
    const copy = new Date(date.getTime());
    copy.setHours(0, 0, 0, 0);
    return copy;
}

function addLocalDays(date, days) {
    const copy = new Date(date.getTime());
    copy.setDate(copy.getDate() + days);
    return copy;
}

function buildMonthCursorRange(date) {
    const cursor = ensureDate(date);
    if (!cursor) {
        return null;
    }
    const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1, 0, 0, 0, 0);
    return { start, end };
}

const CALENDAR_ICON = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const CHANNEL_ICON = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="3 4 21 4 14 13 14 19 10 21 10 13 3 4" />
    </svg>
);

const PLUS_ICON = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const VIEW_OPTIONS = [
    { id: "dayGridMonth" },
    { id: "timeGridWeek" },
    { id: "timeGridDay" },
    { id: "listWeek" },
];

const DEFAULT_VIEW = "timeGridWeek";
const DEFAULT_DURATION_MINUTES = 60;
const DAY_MS = 24 * 60 * 60 * 1000;

function resolveMonthEventMutation(event, targetDate, action = "move") {
    const targetDay = ensureDate(targetDate);
    const originalStart = ensureDate(event?.startAt);
    if (!targetDay || !originalStart) {
        return null;
    }

    const allDay = Boolean(event?.allDay);
    let originalEnd = ensureDate(event?.endAt);
    if (!originalEnd || originalEnd <= originalStart) {
        originalEnd = allDay
            ? addLocalDays(startOfLocalDay(originalStart), 1)
            : new Date(originalStart.getTime() + DEFAULT_DURATION_MINUTES * 60000);
    }

    if (action === "resize-end") {
        const nextStart = allDay ? startOfLocalDay(originalStart) : new Date(originalStart.getTime());
        let nextEnd;
        if (allDay) {
            nextEnd = addLocalDays(startOfLocalDay(targetDay), 1);
            if (nextEnd <= nextStart) {
                nextEnd = addLocalDays(nextStart, 1);
            }
        } else {
            nextEnd = startOfLocalDay(targetDay);
            nextEnd.setHours(
                originalEnd.getHours(),
                originalEnd.getMinutes(),
                originalEnd.getSeconds(),
                originalEnd.getMilliseconds()
            );
            if (nextEnd <= nextStart) {
                nextEnd = new Date(nextStart.getTime() + Math.max(
                    originalEnd.getTime() - originalStart.getTime(),
                    DEFAULT_DURATION_MINUTES * 60000
                ));
            }
        }
        return { start: nextStart, end: nextEnd, allDay };
    }

    const nextStart = startOfLocalDay(targetDay);
    if (!allDay) {
        nextStart.setHours(
            originalStart.getHours(),
            originalStart.getMinutes(),
            originalStart.getSeconds(),
            originalStart.getMilliseconds()
        );
    }
    const durationMs = allDay
        ? Math.max(
            1,
            Math.round((startOfLocalDay(originalEnd).getTime() - startOfLocalDay(originalStart).getTime()) / DAY_MS)
        ) * DAY_MS
        : Math.max(originalEnd.getTime() - originalStart.getTime(), DEFAULT_DURATION_MINUTES * 60000);
    const nextEnd = allDay
        ? addLocalDays(nextStart, Math.max(1, Math.round(durationMs / DAY_MS)))
        : new Date(nextStart.getTime() + durationMs);
    return { start: nextStart, end: nextEnd, allDay };
}

const resolveEventColor = (token) => resolveCalendarColor(token);

const MANAGEMENT_ROLES = ["OWNER", "ADMIN", "MODERATOR"];

const RSVP_ACTIONS = [
    { status: "ACCEPTED", variant: "success" },
    { status: "TENTATIVE", variant: "secondary" },
    { status: "DECLINED", variant: "danger" },
];

const calendarEventPropType = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string,
    startAt: PropTypes.string,
    endAt: PropTypes.string,
    allDay: PropTypes.bool,
    location: PropTypes.string,
    colorToken: PropTypes.string,
    description: PropTypes.string,
    attendees: PropTypes.array,
});

function BandCalendarPanel({
    calendars = [],
    selectedCalendarId,
    onSelectCalendar,
    events = [],
    allEvents = events,
    channels = [],
    channelFilter = null,
    onChannelFilterChange,
    members = [],
    currentUserId,
    error,
    isLoading = false,
    currentView,
    onViewChange,
    onRangeChange,
    onRequestCreate,
    onRequestEdit,
    onEventMove,
    eventDraft,
    onDraftChange,
    isFormOpen = false,
    onCloseForm,
    onSubmit,
    isSubmitting = false,
    formError,
    editingEventId,
    onRequestDelete,
    onRsvp,
    isRsvpSubmitting = false,
    rsvpError,
    targetRange,
}) {
    const { t } = useTranslation();
    const calendarRef = useRef(null);
    const hasCalendars = Array.isArray(calendars) && calendars.length > 0;
    const hasChannelFilter = Array.isArray(channels) && channels.length > 0;
    const activeView = currentView || DEFAULT_VIEW;
    const canCreateEvents = Boolean(selectedCalendarId && onRequestCreate);
    const canMutateEvents = Boolean(selectedCalendarId && onEventMove);
    const ignoreNextDatesSetRef = useRef(false);
    const [currentCalendarRange, setCurrentCalendarRange] = useState(null);
    const colorTokenFieldId = useId();
    const colorTokenLabelId = `${colorTokenFieldId}-label`;
    const participantsFieldId = useId();
    const participantsLabelId = `${participantsFieldId}-label`;
    const normalizedCurrentUserId = currentUserId != null ? String(currentUserId) : null;
    const channelFilterValue = channelFilter != null ? String(channelFilter) : "";
    const viewOptions = useMemo(() => VIEW_OPTIONS.map((option) => ({
        ...option,
        label: t(`bandsPage.calendar.views.${option.id}`),
    })), [t]);
    const rsvpActions = useMemo(() => RSVP_ACTIONS.map((action) => ({
        ...action,
        label: t(`bandsPage.calendar.rsvp.actions.${action.status.toLowerCase()}`),
    })), [t]);
    const getBadgeLabel = useCallback((status) => t(`bandsPage.calendar.rsvp.badges.${(status || "pending").toLowerCase()}`), [t]);
    const getStatusSummary = useCallback((status) => t(`bandsPage.calendar.rsvp.summary.${(status || "pending").toLowerCase()}`), [t]);

    const calendarEvents = useMemo(() => {
        if (!Array.isArray(events)) {
            return [];
        }
        return events
            .filter((event) => event && event.id)
            .map((event) => {
                const color = resolveEventColor(event.colorToken);
                const attendees = Array.isArray(event.attendees) ? event.attendees : [];
                const selfAttendee = normalizedCurrentUserId
                    ? attendees.find((attendee) => String(attendee?.userId) === normalizedCurrentUserId)
                    : null;
                const invitationStatus = selfAttendee?.responseStatus || null;
                const isOwner = normalizedCurrentUserId
                    && event.createdBy != null
                    && String(event.createdBy) === normalizedCurrentUserId;
                const classNames = ["orbit-calendar__event"];
                if (invitationStatus === "PENDING" && !isOwner) {
                    classNames.push("orbit-calendar__event--pending");
                }
                if (isOwner) {
                    classNames.push("orbit-calendar__event--owner");
                }
                return {
                    id: String(event.id),
                    title: event.title || t("bandsPage.calendar.untitledEvent"),
                    start: event.startAt,
                    end: event.endAt,
                    allDay: Boolean(event.allDay),
                    backgroundColor: color || undefined,
                    borderColor: color || undefined,
                    extendedProps: {
                        event,
                        invitationStatus,
                        isOwner,
                        classNames,
                    },
                };
            });
    }, [events, normalizedCurrentUserId, t]);

    const eventsForLookup = useMemo(
        () => (Array.isArray(allEvents) ? allEvents : events),
        [allEvents, events]
    );

    const resolveEventClassNames = useCallback((info) => (
        info?.event?.extendedProps?.classNames || ["orbit-calendar__event"]
    ), []);

    const monthGridEvents = useMemo(() => (
        Array.isArray(events)
            ? events
                .map((event) => mapBandEventToCalendarEvent(event, normalizedCurrentUserId))
                .filter(Boolean)
            : []
    ), [events, normalizedCurrentUserId]);

    const isMonthView = activeView === MONTH_VIEW_ID;
    const targetMonth = useMemo(() => (
        resolveRangeAnchorDate(targetRange)
        || resolveRangeAnchorDate(currentCalendarRange)
        || undefined
    ), [currentCalendarRange, targetRange]);

    const {
        currentTime: monthCalendarNow,
        monthDate,
        days: monthDays,
        eventsByDay: monthEventsByDay,
        goPrev: monthGoPrev,
        goNext: monthGoNext,
        goToday: monthGoToday,
    } = useEventsCalendarMonth(monthGridEvents, { initialMonth: targetMonth });

    const visibleMonthRange = useMemo(() => {
        const firstRow = monthDays[0] || [];
        const lastRow = monthDays[monthDays.length - 1] || [];
        const firstDay = firstRow[0]?.date;
        const lastDay = lastRow[lastRow.length - 1]?.date;
        if (!firstDay || !lastDay) {
            return null;
        }
        const start = new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate(), 0, 0, 0, 0);
        const end = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate() + 1, 0, 0, 0, 0);
        return {
            startMs: start.getTime(),
            endMs: end.getTime(),
        };
    }, [monthDays]);
    const visibleMonthRangeStartMs = visibleMonthRange?.startMs ?? null;
    const visibleMonthRangeEndMs = visibleMonthRange?.endMs ?? null;
    const fullCalendarInitialDate = useMemo(() => (
        resolveRangeAnchorDate(currentCalendarRange)
        || resolveRangeAnchorDate(targetRange)
        || undefined
    ), [currentCalendarRange, targetRange]);

    useEffect(() => {
        if (
            !isMonthView
            || !hasCalendars
            || !selectedCalendarId
            || typeof onRangeChange !== "function"
            || visibleMonthRangeStartMs === null
            || visibleMonthRangeEndMs === null
        ) {
            return;
        }
        onRangeChange({
            start: new Date(visibleMonthRangeStartMs),
            end: new Date(visibleMonthRangeEndMs),
        });
    }, [
        hasCalendars,
        isMonthView,
        onRangeChange,
        selectedCalendarId,
        visibleMonthRangeEndMs,
        visibleMonthRangeStartMs,
    ]);

    const sideRailNextEvent = useMemo(() => {
        const upcoming = getUpcomingEvents(monthGridEvents, monthCalendarNow, 1);
        return upcoming[0] || null;
    }, [monthGridEvents, monthCalendarNow]);

    const sideRailWeekEvents = useMemo(() => (
        getEventsInNextDays(monthGridEvents, monthCalendarNow, 7)
    ), [monthGridEvents, monthCalendarNow]);

    const handleMonthEventClick = useCallback((calendarEvent) => {
        if (!calendarEvent || !onRequestEdit) return;
        const original = eventsForLookup.find(
            (event) => String(event.id) === String(calendarEvent.id),
        );
        if (original) {
            onRequestEdit(original);
        }
    }, [eventsForLookup, onRequestEdit]);

    const handleTopbarPrev = useCallback(() => {
        if (isMonthView) {
            monthGoPrev();
            return;
        }
        const calendarApi = calendarRef.current?.getApi();
        calendarApi?.prev?.();
    }, [isMonthView, monthGoPrev]);

    const handleTopbarNext = useCallback(() => {
        if (isMonthView) {
            monthGoNext();
            return;
        }
        const calendarApi = calendarRef.current?.getApi();
        calendarApi?.next?.();
    }, [isMonthView, monthGoNext]);

    const handleTopbarToday = useCallback(() => {
        if (isMonthView) {
            monthGoToday();
            return;
        }
        const calendarApi = calendarRef.current?.getApi();
        calendarApi?.today?.();
    }, [isMonthView, monthGoToday]);

    const editingEvent = useMemo(() => {
        if (!editingEventId) {
            return null;
        }
        return eventsForLookup.find((event) => String(event.id) === String(editingEventId)) || null;
    }, [editingEventId, eventsForLookup]);
    const isEditing = Boolean(editingEventId);

    const selfMembership = useMemo(() => {
        if (!normalizedCurrentUserId || !Array.isArray(members)) {
            return null;
        }
        return members.find(
            (member) =>
                member
                && member.status === "ACTIVE"
                && String(member.userId) === normalizedCurrentUserId
        ) || null;
    }, [members, normalizedCurrentUserId]);

    const hasManagementRights = Boolean(
        selfMembership?.role && MANAGEMENT_ROLES.includes(selfMembership.role)
    );

    const editingAttendee = useMemo(() => {
        if (!editingEvent || !Array.isArray(editingEvent.attendees) || !normalizedCurrentUserId) {
            return null;
        }
        return editingEvent.attendees.find(
            (attendee) => attendee && String(attendee.userId) === normalizedCurrentUserId
        ) || null;
    }, [editingEvent, normalizedCurrentUserId]);

    const editingEventOwnerId = editingEvent?.createdBy != null ? String(editingEvent.createdBy) : null;
    const isEditingOwner = Boolean(
        normalizedCurrentUserId && editingEventOwnerId && editingEventOwnerId === normalizedCurrentUserId
    );
    const canEditEventDetails = !editingEvent || hasManagementRights || isEditingOwner;

    const currentResponse = editingAttendee?.responseStatus || null;
    const effectiveResponse = (currentResponse || "PENDING").toUpperCase();
    const responseBadge = getBadgeLabel(effectiveResponse);
    const responseDescription = getStatusSummary(effectiveResponse);
    const responseStatusClass = `calendar-modal__rsvp-status--${effectiveResponse.toLowerCase()}`;
    const shouldShowRsvpSection = Boolean(editingEvent && editingAttendee && onRsvp);

    useEffect(() => {
        setCurrentCalendarRange(null);
    }, [selectedCalendarId]);

    const updateCurrentCalendarRange = useCallback((range) => {
        setCurrentCalendarRange((previous) => {
            const next = normalizeCalendarRange(range?.start, range?.end);
            if (!next) {
                return previous;
            }
            if (
                previous?.start?.getTime?.() === next.start.getTime()
                && previous?.end?.getTime?.() === next.end.getTime()
            ) {
                return previous;
            }
            return next;
        });
    }, []);

    const captureFullCalendarRange = useCallback(() => {
        const calendarApi = calendarRef.current?.getApi();
        if (!calendarApi) {
            return;
        }
        const currentView = calendarApi.view || {};
        const range = normalizeCalendarRange(
            currentView.currentStart || currentView.activeStart || calendarApi.getDate?.(),
            currentView.currentEnd || currentView.activeEnd || currentView.currentStart || calendarApi.getDate?.()
        );
        if (range) {
            updateCurrentCalendarRange(range);
        }
    }, [updateCurrentCalendarRange]);

    const captureMonthCalendarRange = useCallback(() => {
        const range = buildMonthCursorRange(monthDate);
        if (range) {
            updateCurrentCalendarRange(range);
        }
    }, [monthDate, updateCurrentCalendarRange]);

    useEffect(() => {
        if (!targetRange || !targetRange.start) {
            return;
        }
        const targetDate = ensureDate(targetRange.start);
        if (!targetDate) {
            return;
        }
        const calendarApi = calendarRef.current?.getApi();
        if (!calendarApi) {
            return;
        }
        const currentStart = calendarApi.view?.currentStart ? ensureDate(calendarApi.view.currentStart) : null;
        if (currentStart && Math.abs(currentStart.getTime() - targetDate.getTime()) < 1000) {
            return;
        }
        ignoreNextDatesSetRef.current = true;
        calendarApi.gotoDate(targetDate);
    }, [targetRange]);

    useEffect(() => {
        const calendarApi = calendarRef.current?.getApi();
        if (!calendarApi) {
            return;
        }
        const currentType = calendarApi.view?.type;
        if (activeView && currentType !== activeView) {
            calendarApi.changeView(activeView);
        }
    }, [activeView]);

    const handleSelectCalendarChange = useCallback((event) => {
        if (!onSelectCalendar) {
            return;
        }
        const { value } = event.target;
        onSelectCalendar(value || null);
    }, [onSelectCalendar]);

    const handleChannelFilterChange = useCallback((event) => {
        if (!onChannelFilterChange) {
            return;
        }
        const { value } = event.target;
        onChannelFilterChange(value || null);
    }, [onChannelFilterChange]);

    const handleViewToggle = useCallback((viewId) => {
        if (activeView === viewId) return;
        if (viewId === MONTH_VIEW_ID) {
            captureFullCalendarRange();
        } else if (activeView === MONTH_VIEW_ID) {
            captureMonthCalendarRange();
        }
        if (typeof onViewChange === 'function') {
            onViewChange(viewId);
        }
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi && calendarApi.view?.type !== viewId) {
            calendarApi.changeView(viewId);
        }
    }, [activeView, captureFullCalendarRange, captureMonthCalendarRange, onViewChange]);

    const handleDatesSet = useCallback((info) => {
        if (ignoreNextDatesSetRef.current) {
            ignoreNextDatesSetRef.current = false;
        }
        updateCurrentCalendarRange({
            start: info.start,
            end: info.end,
        });
        if (onRangeChange) {
            onRangeChange({ start: info.start, end: info.end });
        }
        if (onViewChange) {
            onViewChange(info.view?.type || DEFAULT_VIEW);
        }
    }, [onRangeChange, onViewChange, updateCurrentCalendarRange]);

    const handleSelect = useCallback((selectionInfo) => {
        if (!canCreateEvents) {
            selectionInfo.view.calendar.unselect();
            return;
        }
        if (onRequestCreate) {
            onRequestCreate({
                start: selectionInfo.start,
                end: selectionInfo.end,
                allDay: selectionInfo.allDay,
            });
        }
    }, [canCreateEvents, onRequestCreate]);

    const handleMonthDateSelect = useCallback((date) => {
        const start = ensureDate(date);
        if (!start) {
            return;
        }
        const monthStart = startOfLocalDay(start);
        handleSelect({
            start: monthStart,
            end: addLocalDays(monthStart, 1),
            allDay: true,
            view: {
                calendar: {
                    unselect: () => {},
                },
            },
        });
    }, [handleSelect]);

    const handleEventMutation = useCallback((mutationInfo) => {
        if (!onEventMove) {
            mutationInfo.revert();
            return;
        }
        onEventMove(
            mutationInfo.event.id,
            {
                start: mutationInfo.event.start,
                end: mutationInfo.event.end,
                allDay: mutationInfo.event.allDay,
            },
            mutationInfo.revert
        );
    }, [onEventMove]);

    const handleMonthEventDrop = useCallback((payload, date) => {
        if (!payload?.eventId) {
            return;
        }
        const original = eventsForLookup.find((event) => String(event.id) === String(payload.eventId));
        const mutation = resolveMonthEventMutation(original, date, payload.action);
        if (!mutation) {
            return;
        }
        handleEventMutation({
            event: {
                id: String(payload.eventId),
                start: mutation.start,
                end: mutation.end,
                allDay: mutation.allDay,
            },
            revert: () => {},
        });
    }, [eventsForLookup, handleEventMutation]);

    const handleEventClick = useCallback((clickInfo) => {
        if (!onRequestEdit) {
            return;
        }
        clickInfo.jsEvent?.preventDefault();
        const original = clickInfo.event.extendedProps?.event
            || eventsForLookup.find((event) => String(event.id) === clickInfo.event.id);
        if (original) {
            onRequestEdit(original);
        }
    }, [eventsForLookup, onRequestEdit]);

    const handleNewEvent = useCallback(() => {
        if (!canCreateEvents || !onRequestCreate) {
            return;
        }
        onRequestCreate({});
        const calendarApi = calendarRef.current?.getApi();
        calendarApi?.unselect?.();
    }, [canCreateEvents, onRequestCreate]);

    const handleFormSubmit = useCallback((event) => {
        event.preventDefault();
        if (isSubmitting) {
            return;
        }
        if (onSubmit) {
            onSubmit();
        }
    }, [isSubmitting, onSubmit]);

    const handleTitleChange = useCallback((event) => {
        onDraftChange?.("title", event.target.value);
    }, [onDraftChange]);

    const handleLocationChange = useCallback((event) => {
        onDraftChange?.("location", event.target.value);
    }, [onDraftChange]);

    const handleConferenceLinkChange = useCallback((event) => {
        onDraftChange?.("conferenceLink", event.target.value);
    }, [onDraftChange]);

    const handleColorTokenChange = useCallback((nextValue) => {
        onDraftChange?.("colorToken", nextValue);
    }, [onDraftChange]);

    const handleAttendeeIdsChange = useCallback((nextValue) => {
        onDraftChange?.("attendeeIds", nextValue);
    }, [onDraftChange]);

    const handleDescriptionChange = useCallback((event) => {
        onDraftChange?.("description", event.target.value);
    }, [onDraftChange]);

    const handleRsvpSelection = useCallback((status) => {
        if (!onRsvp) {
            return;
        }
        const eventId = editingEvent?.id ?? editingEventId;
        if (!eventId || !status || status === currentResponse) {
            return;
        }
        onRsvp(eventId, status);
    }, [currentResponse, editingEvent, editingEventId, onRsvp]);

    const handleStartChange = useCallback((event) => {
        const raw = event.target.value;
        if (!raw) {
            onDraftChange?.("startAt", "");
            return;
        }
        if (eventDraft?.allDay) {
            const startDate = ensureDate(`${raw}T00:00:00`);
            if (!startDate) {
                onDraftChange?.("startAt", "");
                return;
            }
            startDate.setSeconds(0, 0);
            onDraftChange?.("startAt", startDate.toISOString());
            return;
        }
        const start = ensureDate(raw);
        if (!start) {
            onDraftChange?.("startAt", "");
            return;
        }
        onDraftChange?.("startAt", start.toISOString());
    }, [eventDraft?.allDay, onDraftChange]);

    const handleEndChange = useCallback((event) => {
        const raw = event.target.value;
        if (!raw) {
            onDraftChange?.("endAt", "");
            return;
        }
        if (eventDraft?.allDay) {
            const endDate = ensureDate(`${raw}T00:00:00`);
            if (!endDate) {
                onDraftChange?.("endAt", "");
                return;
            }
            endDate.setSeconds(0, 0);
            endDate.setDate(endDate.getDate() + 1);
            onDraftChange?.("endAt", endDate.toISOString());
            return;
        }
        const end = ensureDate(raw);
        if (!end) {
            onDraftChange?.("endAt", "");
            return;
        }
        onDraftChange?.("endAt", end.toISOString());
    }, [eventDraft?.allDay, onDraftChange]);

    const handleAllDayToggle = useCallback((event) => {
        const nextAllDay = event.target.checked;
        onDraftChange?.("allDay", nextAllDay);

        const fallbackRange = defaultTimedEventWindow();
        let start = ensureDate(eventDraft?.startAt) || fallbackRange.start;
        let end = ensureDate(eventDraft?.endAt) || fallbackRange.end;

        if (nextAllDay) {
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);
            if (end <= start) {
                end = new Date(start.getTime());
                end.setDate(end.getDate() + 1);
            }
        } else {
            if (start.getHours() === 0 && start.getMinutes() === 0) {
                start = new Date(fallbackRange.start.getTime());
            }
            if (!end || end <= start) {
                end = new Date(start.getTime() + DEFAULT_DURATION_MINUTES * 60000);
            }
        }

        onDraftChange?.("startAt", start.toISOString());
        onDraftChange?.("endAt", end.toISOString());
    }, [eventDraft?.endAt, eventDraft?.startAt, onDraftChange]);

    const handleDeleteEvent = useCallback(() => {
        if (!onRequestDelete || (!editingEventId && !editingEvent)) {
            return;
        }
        const fallback = editingEvent || {
            id: editingEventId,
            ...eventDraft,
        };
        onRequestDelete(fallback);
    }, [editingEvent, editingEventId, eventDraft, onRequestDelete]);

    const startInputValue = useMemo(() => {
        if (!eventDraft?.startAt) {
            return "";
        }
        if (eventDraft.allDay) {
            return toDateInputValue(eventDraft.startAt);
        }
        return toDateTimeInputValue(eventDraft.startAt);
    }, [eventDraft?.allDay, eventDraft?.startAt]);

    const endInputValue = useMemo(() => {
        if (!eventDraft?.endAt) {
            return "";
        }
        if (eventDraft.allDay) {
            const endDate = ensureDate(eventDraft.endAt);
            if (!endDate) {
                return "";
            }
            endDate.setDate(endDate.getDate() - 1);
            return toDateInputValue(endDate);
        }
        return toDateTimeInputValue(eventDraft.endAt);
    }, [eventDraft?.allDay, eventDraft?.endAt]);

    const disableEventActions = !selectedCalendarId;
    const disableFormFields = isSubmitting || (isEditing && !canEditEventDetails);
    const disableSaveButton = disableFormFields || disableEventActions;

    const calendarOptions = useMemo(() => (
        hasCalendars
            ? calendars.map((calendar) => ({ value: String(calendar.id), label: calendar.name }))
            : []
    ), [calendars, hasCalendars]);

    const channelOptions = useMemo(() => ([
        { value: "", label: t("bandsPage.calendar.selectors.allChannels") },
        ...channels.map((channel) => ({ value: String(channel.id), label: channel.name })),
    ]), [channels, t]);

    const segmentedTabsOptions = useMemo(() => (
        viewOptions.map((option) => ({ value: option.id, label: option.label }))
    ), [viewOptions]);

    return (
        <div className="workspace-calendar">
            <header className="workspace-calendar__topbar">
                <div className="workspace-calendar__selectors">
                    <GlassSelect
                        id="workspace-calendar-select"
                        icon={CALENDAR_ICON}
                        label={t("bandsPage.calendar.selectors.calendar")}
                        value={selectedCalendarId ? String(selectedCalendarId) : ""}
                        options={hasCalendars ? calendarOptions : [{ value: "", label: t("bandsPage.calendar.selectors.none") }]}
                        onChange={handleSelectCalendarChange}
                        disabled={!hasCalendars}
                    />
                    {hasChannelFilter && (
                        <GlassSelect
                            id="workspace-calendar-channel-filter"
                            icon={CHANNEL_ICON}
                            label={t("bandsPage.calendar.selectors.channel")}
                            value={channelFilterValue}
                            options={channelOptions}
                            onChange={handleChannelFilterChange}
                        />
                    )}
                </div>
                <div className="workspace-calendar__actions">
                    <SegmentedTabs
                        options={segmentedTabsOptions}
                        value={activeView}
                        onChange={handleViewToggle}
                        ariaLabel={t("bandsPage.calendar.viewToggleAria")}
                    />
                    <span className="workspace-calendar__divider" aria-hidden="true" />
                    <div
                        className="workspace-calendar__nav"
                        role="group"
                        aria-label={t("bandsPage.calendar.navigation.ariaLabel")}
                    >
                        <IconButton
                            icon={<ChevronIcon direction="left" />}
                            label={t("bandsPage.calendar.navigation.previous")}
                            variant="glass"
                            size={34}
                            onClick={handleTopbarPrev}
                            disabled={!hasCalendars}
                        />
                        <button
                            type="button"
                            className="workspace-calendar__today"
                            onClick={handleTopbarToday}
                            disabled={!hasCalendars}
                        >
                            {t("bandsPage.calendar.today")}
                        </button>
                        <IconButton
                            icon={<ChevronIcon direction="right" />}
                            label={t("bandsPage.calendar.navigation.next")}
                            variant="glass"
                            size={34}
                            onClick={handleTopbarNext}
                            disabled={!hasCalendars}
                        />
                    </div>
                    <span className="workspace-calendar__divider" aria-hidden="true" />
                    <Button
                        variant="gradient"
                        size="medium"
                        onClick={handleNewEvent}
                        disabled={!canCreateEvents || disableEventActions}
                        className="workspace-calendar__new-event"
                    >
                        {PLUS_ICON}
                        {t("bandsPage.calendar.newEvent")}
                    </Button>
                </div>
            </header>
            {error && (
                <div className="orbit-calendar__error" role="alert">
                    {error}
                </div>
            )}
            <div
                className={`workspace-calendar__body${
                    isMonthView && hasCalendars && selectedCalendarId ? " workspace-calendar__body--month" : ""
                }`}
            >
                {hasCalendars ? (
                    <>
                        {isMonthView && selectedCalendarId ? (
                            <>
                                <CalendarMonthGrid
                                    monthDate={monthDate}
                                    days={monthDays}
                                    eventsByDay={monthEventsByDay}
                                    onEventClick={handleMonthEventClick}
                                    onDateSelect={handleMonthDateSelect}
                                    onEventDrop={handleMonthEventDrop}
                                    canSelectDates={canCreateEvents}
                                    canMutateEvents={canMutateEvents}
                                    onPrev={monthGoPrev}
                                    onNext={monthGoNext}
                                    onToday={monthGoToday}
                                    className="workspace-calendar__month-main"
                                />
                                <aside className="workspace-calendar__rail">
                                    <CalendarNextEventCard
                                        event={sideRailNextEvent}
                                        onOpen={handleMonthEventClick}
                                        ctaAction="open"
                                        ctaLabel={t("home.postCard.relatedObject.actions.viewDetails")}
                                        emptyLabel={t("bandsPage.calendar.empty.month")}
                                    />
                                    <CalendarThisWeekList
                                        events={sideRailWeekEvents}
                                        onOpen={handleMonthEventClick}
                                        emptyLabel={t("bandsPage.calendar.empty.week")}
                                    />
                                </aside>
                            </>
                        ) : (
                            <div className="orbit-calendar__calendar">
                                <FullCalendar
                                    key={selectedCalendarId || "orbit-calendar"}
                                    ref={calendarRef}
                                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                                    initialView={activeView}
                                    initialDate={fullCalendarInitialDate}
                                    headerToolbar={false}
                                    height="100%"
                                    selectable={canCreateEvents}
                                    selectMirror
                                    editable={canMutateEvents}
                                    eventDurationEditable={canMutateEvents}
                                    eventStartEditable={canMutateEvents}
                                    eventResizableFromStart={canMutateEvents}
                                    events={calendarEvents}
                                    select={handleSelect}
                                    eventDrop={handleEventMutation}
                                    eventResize={handleEventMutation}
                                    eventClick={handleEventClick}
                                    datesSet={handleDatesSet}
                                    slotDuration="00:30:00"
                                    slotLabelFormat={{ hour: "numeric", minute: "2-digit" }}
                                    eventTimeFormat={{ hour: "numeric", minute: "2-digit", meridiem: "short" }}
                                    dayMaxEvents={3}
                                    nowIndicator
                                    expandRows
                                    firstDay={1}
                                    navLinks
                                    eventDisplay="block"
                                    eventClassNames={resolveEventClassNames}
                                />
                                {isLoading && (
                                    <div className="orbit-calendar__overlay" aria-hidden="true">
                                        <LoadingSpinner size={40} />
                                    </div>
                                )}
                                {!isLoading && selectedCalendarId && calendarEvents.length === 0 && (
                                    <div className="orbit-calendar__empty" role="status">
                                        <h3>{t("bandsPage.calendar.empty.noEvents.title")}</h3>
                                        <p>{t("bandsPage.calendar.empty.noEvents.subtitle")}</p>
                                    </div>
                                )}
                                {!selectedCalendarId && (
                                    <div className="orbit-calendar__empty orbit-calendar__empty--select" role="status">
                                        <h3>{t("bandsPage.calendar.empty.selectCalendar.title")}</h3>
                                        <p>{t("bandsPage.calendar.empty.selectCalendar.subtitle")}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="orbit-calendar__empty orbit-calendar__empty--no-cal" role="status">
                        <h3>{t("bandsPage.calendar.empty.noCalendars.title")}</h3>
                        <p>{t("bandsPage.calendar.empty.noCalendars.subtitle")}</p>
                    </div>
                )}
            </div>

            {isFormOpen && (
                <div className="calendar-modal" role="dialog" aria-modal="true" aria-labelledby="calendarFormTitle">
                    <div className="calendar-modal__sheet">
                        <header className="calendar-modal__hero">
                            <div className="calendar-modal__hero-overlay" aria-hidden="true" />
                            <span className="calendar-modal__chip">
                                {isEditing
                                    ? t("bandsPage.calendar.modal.title.edit")
                                    : t("bandsPage.calendar.modal.title.create")}
                            </span>
                            <h2 id="calendarFormTitle" className="calendar-modal__title">
                                {(isEditing && eventDraft?.title)
                                    ? eventDraft.title
                                    : t("bandsPage.calendar.modal.title.create")}
                            </h2>
                            <button
                                type="button"
                                className="calendar-modal__close"
                                onClick={onCloseForm}
                                aria-label={t("bandsPage.common.close")}
                            >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </header>
                        <form className="calendar-modal__body" onSubmit={handleFormSubmit}>
                            <div className="calendar-modal__form">
                                <label className="calendar-modal__field calendar-modal__field--wide">
                                    <span className="calendar-modal__label">{t("bandsPage.calendar.modal.fields.title")}</span>
                                    <input
                                        className="calendar-modal__input"
                                        value={eventDraft?.title || ""}
                                        onChange={handleTitleChange}
                                        maxLength={140}
                                        required
                                        disabled={disableFormFields}
                                    />
                                </label>
                                <label className="calendar-modal__field calendar-modal__field--wide calendar-modal__field--checkbox">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(eventDraft?.allDay)}
                                        onChange={handleAllDayToggle}
                                        disabled={disableFormFields}
                                    />
                                    <span className="calendar-modal__label">{t("bandsPage.calendar.modal.fields.allDay")}</span>
                                </label>
                                <div className="calendar-modal__field">
                                    <span className="calendar-modal__label">{t("bandsPage.calendar.modal.fields.start")}</span>
                                    <GlassDatePicker
                                        label={t("bandsPage.calendar.modal.fields.start")}
                                        value={startInputValue}
                                        onChange={handleStartChange}
                                        withTime={!eventDraft?.allDay}
                                        required
                                        disabled={disableFormFields}
                                    />
                                </div>
                                <div className="calendar-modal__field">
                                    <span className="calendar-modal__label">{t("bandsPage.calendar.modal.fields.end")}</span>
                                    <GlassDatePicker
                                        label={t("bandsPage.calendar.modal.fields.end")}
                                        value={endInputValue}
                                        onChange={handleEndChange}
                                        withTime={!eventDraft?.allDay}
                                        required
                                        disabled={disableFormFields}
                                    />
                                </div>
                                <label className="calendar-modal__field">
                                    <span className="calendar-modal__label">{t("bandsPage.calendar.modal.fields.location")}</span>
                                    <input
                                        className="calendar-modal__input"
                                        value={eventDraft?.location || ""}
                                        onChange={handleLocationChange}
                                        placeholder={t("bandsPage.calendar.modal.placeholders.location")}
                                        maxLength={200}
                                        disabled={disableFormFields}
                                    />
                                </label>
                                <label className="calendar-modal__field">
                                    <span className="calendar-modal__label">{t("bandsPage.calendar.modal.fields.conference")}</span>
                                    <input
                                        className="calendar-modal__input"
                                        type="url"
                                        value={eventDraft?.conferenceLink || ""}
                                        onChange={handleConferenceLinkChange}
                                        placeholder={t("bandsPage.calendar.modal.placeholders.conference")}
                                        disabled={disableFormFields}
                                    />
                                </label>
                                <label
                                    className="calendar-modal__field calendar-modal__field--color"
                                    htmlFor={colorTokenFieldId}
                                >
                                    <span id={colorTokenLabelId} className="calendar-modal__label">
                                        {t("bandsPage.calendar.modal.fields.color")}
                                    </span>
                                    <ColorTokenPicker
                                        id={colorTokenFieldId}
                                        ariaLabelledby={colorTokenLabelId}
                                        value={eventDraft?.colorToken || ""}
                                        onChange={handleColorTokenChange}
                                        disabled={disableFormFields}
                                    />
                                </label>
                                <label
                                    className="calendar-modal__field calendar-modal__field--wide calendar-modal__field--participants"
                                    htmlFor={participantsFieldId}
                                >
                                    <span id={participantsLabelId} className="calendar-modal__label">
                                        {t("bandsPage.calendar.modal.fields.participants")}
                                    </span>
                                    <EventParticipantsPicker
                                        id={participantsFieldId}
                                        ariaLabelledby={participantsLabelId}
                                        members={members}
                                        value={eventDraft?.attendeeIds || []}
                                        onChange={handleAttendeeIdsChange}
                                        disabled={disableFormFields}
                                        currentUserId={currentUserId}
                                    />
                                </label>
                                <label className="calendar-modal__field calendar-modal__field--wide">
                                    <span className="calendar-modal__label">{t("bandsPage.calendar.modal.fields.description")}</span>
                                    <textarea
                                        className="calendar-modal__textarea"
                                        value={eventDraft?.description || ""}
                                        onChange={handleDescriptionChange}
                                        placeholder={t("bandsPage.calendar.modal.placeholders.description")}
                                        maxLength={2000}
                                        disabled={disableFormFields}
                                    />
                                </label>
                            </div>

                            {shouldShowRsvpSection && (
                                <section className="calendar-modal__rsvp" aria-label={t("bandsPage.calendar.modal.rsvp.sectionAria")}>
                                    <div className="calendar-modal__rsvp-summary">
                                        <h3>{t("bandsPage.calendar.modal.rsvp.title")}</h3>
                                        <span
                                            className={`calendar-modal__rsvp-status ${responseStatusClass}`}
                                        >
                                            {responseBadge}
                                        </span>
                                    </div>
                                    <p className="calendar-modal__rsvp-description" aria-live="polite">
                                        {responseDescription}
                                    </p>
                                    {isEditing && !canEditEventDetails && (
                                        <p className="calendar-modal__rsvp-note">
                                            {t("bandsPage.calendar.modal.rsvp.editRestriction")}
                                        </p>
                                    )}
                                    <div
                                        className="calendar-modal__rsvp-actions"
                                        role="group"
                                        aria-label={t("bandsPage.calendar.modal.rsvp.actionsAria")}
                                    >
                                        {rsvpActions.map((action) => {
                                            const isSelected = effectiveResponse === action.status;
                                            return (
                                                <button
                                                    key={action.status}
                                                    type="button"
                                                    className={`calendar-modal__rsvp-button calendar-modal__rsvp-button--${action.status.toLowerCase()}${
                                                        isSelected ? " is-active" : ""
                                                    }`}
                                                    onClick={() => handleRsvpSelection(action.status)}
                                                    disabled={isRsvpSubmitting}
                                                    aria-pressed={isSelected}
                                                    data-status={action.status.toLowerCase()}
                                                >
                                                    {action.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {isRsvpSubmitting && (
                                        <span className="calendar-modal__rsvp-hint" aria-live="polite">
                                            {t("bandsPage.calendar.modal.rsvp.updating")}
                                        </span>
                                    )}
                                    {rsvpError && (
                                        <div className="calendar-modal__rsvp-error" role="alert">
                                            {rsvpError}
                                        </div>
                                    )}
                                </section>
                            )}

                            {formError && (
                                <div className="calendar-modal__error" role="alert">
                                    {formError}
                                </div>
                            )}

                            <div className="calendar-modal__actions">
                                {isEditing && canEditEventDetails && (
                                    <button
                                        type="button"
                                        className="calendar-modal__action-btn calendar-modal__action-btn--delete"
                                        onClick={handleDeleteEvent}
                                        disabled={isSubmitting}
                                    >
                                        {t("bandsPage.calendar.modal.actions.delete")}
                                    </button>
                                )}
                                <div className="calendar-modal__actions-right">
                                    <button
                                        type="button"
                                        className="calendar-modal__action-btn calendar-modal__action-btn--secondary"
                                        onClick={onCloseForm}
                                        disabled={isSubmitting}
                                    >
                                        {t("bandsPage.calendar.modal.actions.cancel")}
                                    </button>
                                    <button
                                        type="submit"
                                        className="calendar-modal__action-btn calendar-modal__action-btn--primary"
                                        disabled={disableSaveButton}
                                    >
                                        {isSubmitting
                                            ? t("bandsPage.calendar.modal.actions.saving")
                                            : isEditing
                                                ? t("bandsPage.calendar.modal.actions.saveChanges")
                                                : t("bandsPage.calendar.modal.actions.create")}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

BandCalendarPanel.propTypes = {
    calendars: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
    })),
    selectedCalendarId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSelectCalendar: PropTypes.func,
    events: PropTypes.arrayOf(calendarEventPropType),
    allEvents: PropTypes.arrayOf(calendarEventPropType),
    channels: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
    })),
    channelFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChannelFilterChange: PropTypes.func,
    members: PropTypes.arrayOf(PropTypes.shape({
        membershipId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        userId: PropTypes.string,
        role: PropTypes.string,
        status: PropTypes.string,
    })),
    currentUserId: PropTypes.string,
    error: PropTypes.string,
    isLoading: PropTypes.bool,
    currentView: PropTypes.string,
    onViewChange: PropTypes.func,
    onRangeChange: PropTypes.func,
    onRequestCreate: PropTypes.func,
    onRequestEdit: PropTypes.func,
    onEventMove: PropTypes.func,
    eventDraft: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        startAt: PropTypes.string,
        endAt: PropTypes.string,
        allDay: PropTypes.bool,
        location: PropTypes.string,
        conferenceLink: PropTypes.string,
        colorToken: PropTypes.string,
        attendeeIds: PropTypes.arrayOf(PropTypes.string),
    }),
    onDraftChange: PropTypes.func,
    isFormOpen: PropTypes.bool,
    onCloseForm: PropTypes.func,
    onSubmit: PropTypes.func,
    isSubmitting: PropTypes.bool,
    formError: PropTypes.string,
    editingEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onRequestDelete: PropTypes.func,
    onRsvp: PropTypes.func,
    isRsvpSubmitting: PropTypes.bool,
    rsvpError: PropTypes.string,
    targetRange: PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string,
    }),
};

export default BandCalendarPanel;
